import type { BlogPost } from './types'

export const WARUM_KEINE_EMAIL_REGISTRIERUNG: BlogPost = {
  slug: 'warum-keine-email-registrierung',
  lang: 'de',
  topicId: 'warum-keine-email-registrierung',
  title: 'Warum Roofora auf E-Mail verzichtet',
  metaDescription:
    'Bei Roofora registrierst du dich nur mit Username und Passwort - keine E-Mail-Adresse, keine ' +
    'Telefonnummer. Warum wir das so gebaut haben.',
  keywords: [
    'Solaranlage Angebote ohne E-Mail',
    'Handwerkerportal ohne Anmeldung mit E-Mail',
    'PV Angebote anonym einholen',
    'Solaranlage Angebote ohne Anruf-Flut',
    'Datenschutz Solaranfrage',
  ],
  publishedAt: '2026-07-09',
  excerpt:
    'Für ein Roofora-Konto brauchst du nur einen Username und ein Passwort - keine E-Mail-Adresse. ' +
    'Das ist kein Zufall, sondern der Kern unseres Datenschutz-Versprechens.',
  sections: [
    {
      type: 'paragraph',
      text:
        'Wer schon einmal ein Angebot für eine Solaranlage angefragt hat, kennt vermutlich das ' +
        'Muster: Man trägt seine E-Mail-Adresse und Telefonnummer in ein Formular ein - und ' +
        'innerhalb weniger Tage klingelt das Telefon im Minutentakt. Die E-Mail-Adresse wandert ' +
        'an Vertriebspartner weiter, landet in Newslettern, die man nie abonniert hat, und ' +
        'irgendwann weiß man nicht mehr, welcher der zehn Anrufer eigentlich das ursprünglich ' +
        'angefragte Angebot gestellt hat. Genau dieses Problem wollten wir bei Roofora an der ' +
        'Wurzel packen - und die Wurzel ist die E-Mail-Adresse selbst.',
    },
    {
      type: 'heading',
      text: 'Eine E-Mail-Adresse ist schon ein Kontaktkanal',
    },
    {
      type: 'paragraph',
      text:
        'Die meisten Solar- und Handwerkerportale verlangen E-Mail und Telefonnummer schon beim ' +
        'Anlegen eines Kontos - lange bevor überhaupt ein Handwerksbetrieb ein konkretes Angebot ' +
        'abgegeben hat. Ab diesem Moment ist die Adresse in einer Datenbank, auf die potenziell ' +
        'mehrere Vertriebspartner Zugriff haben. Ob und wie oft sie kontaktiert wird, entzieht ' +
        'sich meist der Kontrolle der anfragenden Person. Das Grundproblem ist also nicht "zu ' +
        'viele Anrufe", sondern dass Kontaktdaten weitergegeben werden, bevor überhaupt eine ' +
        'informierte Entscheidung getroffen wurde, wem man vertrauen möchte.',
    },
    {
      type: 'heading',
      text: 'Wie die Registrierung bei Roofora wirklich funktioniert',
    },
    {
      type: 'paragraph',
      text:
        'Für ein Kundenkonto bei Roofora brauchst du genau zwei Dinge: einen Username und ein ' +
        'Passwort. Das war\'s. Keine E-Mail-Adresse, keine Telefonnummer, keine Postanschrift. ' +
        'Es gibt in unserem Registrierungsformular schlicht kein Feld dafür - du kannst diese ' +
        'Daten an dieser Stelle also weder eintragen noch versehentlich preisgeben. Du kannst ' +
        'sofort eine Anfrage für deine Solaranlage stellen, Angebote von Handwerksbetrieben ' +
        'vergleichen und die gesamte Vermittlung anonym durchlaufen, ohne dass irgendjemand ' +
        'weiß, wer du bist oder wie man dich erreicht.',
    },
    {
      type: 'heading',
      text: 'Und was ist mit "Passwort vergessen"?',
    },
    {
      type: 'paragraph',
      text:
        'Der naheliegende Einwand: Ohne E-Mail-Adresse gibt es doch normalerweise auch keinen Weg, ' +
        'ein vergessenes Passwort zurückzusetzen. Deshalb bekommst du bei der Registrierung einen ' +
        'einmaligen Wiederherstellungscode angezeigt, den du dir notieren kannst - ähnlich einem ' +
        'Recovery-Key, wie man ihn von Passwort-Managern kennt. Damit kannst du dein Konto auch ' +
        'ohne hinterlegte E-Mail-Adresse wiederherstellen. Der Kompromiss ist bewusst: eine ' +
        'Nutzerin oder ein Nutzer trägt selbst die Verantwortung für den Code, dafür verlässt ' +
        'keine Kontaktinformation ungefragt unsere Datenbank.',
    },
    {
      type: 'heading',
      text: 'Wann wird meine E-Mail überhaupt gebraucht?',
    },
    {
      type: 'paragraph',
      text:
        'Erst dann, wenn du es aktiv willst - und auch dann nur für genau den einen Handwerksbetrieb, ' +
        'den du auswählst. Möchte ein Betrieb nach Ansicht deiner (weiterhin anonymen) Anfrage und ' +
        'seines Angebots mit dir in Kontakt treten, entscheidest du individuell, ob du diesen ' +
        'Betrieb zulässt. Erst nach dieser Freigabe hinterlegst du E-Mail-Adresse oder ' +
        'Telefonnummer für genau diesen einen Kontakt. Keine Sammelfreigabe, keine automatische ' +
        'Weitergabe an weitere Firmen.',
    },
    {
      type: 'heading',
      text: 'Das Ergebnis: Stille im Postfach, bis du es anders willst',
    },
    {
      type: 'list',
      items: [
        'Kein Spam-Postfach nach der ersten Anfrage, weil zu diesem Zeitpunkt noch gar keine E-Mail-Adresse existiert',
        'Keine Kaltakquise-Anrufe, weil niemand deine Telefonnummer kennt, bis du sie für einen bestimmten Betrieb freigibst',
        'Volle Kontrolle: du entscheidest pro Handwerksbetrieb einzeln, nicht pauschal für alle Anbieter auf der Plattform',
        'Angebote vergleichen weiterhin uneingeschränkt möglich - die Anonymität kostet dich keine Funktion',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Das ist der Unterschied zwischen "Datenschutz als Feature in der Datenschutzerklärung" ' +
        'und "Datenschutz als Voreinstellung im Code": Wenn ein Feld gar nicht existiert, kann es ' +
        'auch nicht missbraucht werden. Wenn du Solaranlagen-Angebote vergleichen möchtest, ohne ' +
        'danach von Vertrieb überrannt zu werden, probier Roofora aus - Registrierung dauert unter ' +
        'einer Minute, und wir fragen dich dabei nicht einmal nach deiner E-Mail-Adresse.',
    },
  ],
  relatedLinks: [
    { label: 'Photovoltaik-Anfrage erstellen', href: '/photovoltaik-anfrage-erstellen' },
    { label: 'PV-Installateur finden', href: '/pv-installateur-finden' },
    { label: 'Photovoltaik-Angebote ganz ohne Telefonnummer oder E-Mail', href: '/photovoltaik-ohne-telefonnummer' },
  ],
}
